import { createContext, useContext, useState, ReactNode, useEffect, useRef, useMemo, useCallback } from "react";
import { MachineEvent } from "@/machines/types/event";
import { createCursorStore, CursorStore, CursorStoreContext } from "@/machines/context/cursor-store.ts";

type CurrentEventsValue = {
  layers: Record<string, MachineEvent>;
  truck_event?: MachineEvent;
} | null;

// Shallow-equal two currentEvents snapshots by event IDENTITY. getCurrentEvents
// returns references into the pre-sorted event arrays, so within one event the
// SAME event refs come back — letting the recompute skip a no-op state write (and
// the re-render of every context consumer) on the ~30x/sec playback ticks that
// don't cross an event boundary. New data (a poll) rebuilds the arrays → new refs
// → this correctly reports a change.
const sameCurrentEvents = (a: CurrentEventsValue, b: CurrentEventsValue): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.truck_event !== b.truck_event) return false;
  const aKeys = Object.keys(a.layers);
  const bKeys = Object.keys(b.layers);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (a.layers[key] !== b.layers[key]) return false;
  }
  return true;
};

interface TimelineContextType {
  /**
   * Stable cursor writer. The cursor VALUE (`selectedTime`/`lastOrigin`) no
   * longer lives here — it moved to the per-instance {@link CursorStore} to stop
   * the 30×/sec live-playback re-render storm. Read the value with
   * `useSelectedTime()` / `useLastOrigin()` (render) or `useCursorEffect()`
   * (effect-only). This writer keeps stable identity, so writer-only consumers
   * (events table, seek-to-timestamp) need no change.
   */
  setSelectedTime: (time: number | null, origin?: "internal" | "external" | "video") => void;
  currentEvents: {
    layers: Record<string, MachineEvent>;
    truck_event?: MachineEvent;
  } | null;
  selectedTrack: string | null;
  setSelectedTrack: (trackKey: string | null) => void;
  isTrackLocked: boolean;
  setIsTrackLocked: (locked: boolean) => void;
  isEditMode: boolean;
  setIsEditMode: (editMode: boolean) => void;
  selectedEventForEdit: MachineEvent | null;
  setSelectedEventForEdit: (event: MachineEvent | null) => void;
  createStartTime: number | null;
  setCreateStartTime: (time: number | null) => void;
  isVideoPlaying: boolean;
  setIsVideoPlaying: (playing: boolean) => void;
  isVideoLinked: boolean;
  setIsVideoLinked: (linked: boolean) => void;
  videoPlaybackRate: number;
  setVideoPlaybackRate: (rate: number) => void;
  centerRequest: number;
  requestCenter: () => void;
  /**
   * Bumped to command the PRIMARY video to start playing (e.g. the map live
   * view's "Go live"). Only the primary tile acts on it; followers mirror it.
   */
  playRequest: number;
  requestPlay: () => void;
  /**
   * The mirror of {@link playRequest}: bumped to command the PRIMARY video to
   * PAUSE. A follower tile owns no playback authority (the primary drives the
   * shared cursor and the followers mirror it), so its pause button has to
   * command the primary and let the existing mirror effects carry every tile.
   * Ignored (starts at 0) wherever nothing calls it — machine-detail included.
   */
  pauseRequest: number;
  requestPause: () => void;
  /**
   * Set to a timestamp when something (e.g. the ML-export deep-link) wants the
   * on-map event overlay opened for the event at that time. The timeline
   * section consumes it once resolved, then clears it back to null.
   */
  eventOverlayRequestTime: number | null;
  requestEventOverlay: (time: number | null) => void;
}

export const TimelineContext = createContext<TimelineContextType | undefined>(undefined);

/** What `useTimelineProviderValue` returns: the monolithic value + the cursor store. */
export interface TimelineProviderValue {
  value: TimelineContextType;
  cursorStore: CursorStore;
}

/**
 * Builds the full timeline context value (edit state, video sync, …) plus the
 * per-instance cursor store. Extracted from `TimelineProvider` so a consumer can
 * render bare `CursorStoreContext.Provider` + `TimelineContext.Provider` while
 * keeping stable element types — the map stack's row provider uses this to
 * forward the ambient store + value for its active row without changing element
 * type on an active-machine switch (which would otherwise remount the row).
 *
 * `trackCurrentEvents` gates the per-cursor `currentEvents` recompute. It has
 * render consumers on machine-detail (default on); `/map` has none, so it passes
 * false to skip the wasted per-frame computation entirely.
 */
export const useTimelineProviderValue = (
  events?: MachineEvent[],
  truckEvents?: MachineEvent[],
  { trackCurrentEvents = true }: { trackCurrentEvents?: boolean } = {},
): TimelineProviderValue => {
  // Lazy-create the cursor store ONCE per provider instance. `useRef(create())`
  // would allocate a throwaway store on every render — this creates exactly one.
  const cursorStoreRef = useRef<CursorStore>();
  if (!cursorStoreRef.current) cursorStoreRef.current = createCursorStore();
  const cursorStore = cursorStoreRef.current;

  // Stable cursor writer delegating to the store, mirrored onto the monolithic
  // value so writer-only consumers need no change. (`cursorStore` is stable.)
  const setSelectedTime = useCallback(
    (time: number | null, origin: "internal" | "external" | "video" = "internal") => {
      cursorStore.setSelectedTime(time, origin);
    },
    [cursorStore],
  );

  const [currentEvents, setCurrentEvents] = useState<{
    layers: Record<string, MachineEvent>;
    truck_event?: MachineEvent;
  } | null>(null);

  const [selectedTrack, setSelectedTrackState] = useState<string | null>(null);
  const [isTrackLocked, setIsTrackLockedState] = useState(false);
  const [isEditMode, setIsEditModeState] = useState(false);
  const [selectedEventForEdit, setSelectedEventForEditState] = useState<MachineEvent | null>(null);
  const [createStartTime, setCreateStartTimeState] = useState<number | null>(null);
  const [isVideoPlaying, setIsVideoPlayingState] = useState(false);
  const [isVideoLinked, setIsVideoLinkedState] = useState(true);
  const [videoPlaybackRate, setVideoPlaybackRateState] = useState(1);
  const [centerRequest, setCenterRequest] = useState(0);
  const [playRequest, setPlayRequest] = useState(0);
  const [pauseRequest, setPauseRequest] = useState(0);
  const [eventOverlayRequestTime, setEventOverlayRequestTime] = useState<number | null>(null);

  // Memoize all setters to prevent unnecessary re-renders
  const setSelectedTrack = useCallback((trackKey: string | null) => setSelectedTrackState(trackKey), []);
  const setIsTrackLocked = useCallback((locked: boolean) => setIsTrackLockedState(locked), []);
  const setIsEditMode = useCallback((editMode: boolean) => setIsEditModeState(editMode), []);
  const setSelectedEventForEdit = useCallback((event: MachineEvent | null) => setSelectedEventForEditState(event), []);
  const setCreateStartTime = useCallback((time: number | null) => setCreateStartTimeState(time), []);
  const setIsVideoPlaying = useCallback((playing: boolean) => setIsVideoPlayingState(playing), []);
  const setIsVideoLinked = useCallback((linked: boolean) => setIsVideoLinkedState(linked), []);
  const setVideoPlaybackRate = useCallback((rate: number) => setVideoPlaybackRateState(rate), []);
  const requestCenter = useCallback(() => setCenterRequest(c => c + 1), []);
  const requestPlay = useCallback(() => setPlayRequest(c => c + 1), []);
  const requestPause = useCallback(() => setPauseRequest(c => c + 1), []);
  const requestEventOverlay = useCallback((time: number | null) => setEventOverlayRequestTime(time), []);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const rafRef = useRef<number | null>(null);

  // Pre-sort and group events once when they change (memoized for performance)
  const { sortedGroupedEvents, sortedTruckEvents } = useMemo(() => {
    const layers = events || [];
    const trucks = truckEvents || [];

    // Group events by layer key
    const groupedByLayer: Record<string, MachineEvent[]> = {};
    layers.forEach(e => {
      let key: string = "default";
      if ('state' in e && e.state) {
        key = String(e.state).toLowerCase();
      } else if ('type' in e && e.type) {
        key = String(e.type).toLowerCase();
      } else if ('category' in e && e.category) {
        key = String(e.category).toLowerCase();
      } else if ('name' in e && e.name) {
        key = String(e.name).toLowerCase();
      }
      if (!groupedByLayer[key]) groupedByLayer[key] = [];
      groupedByLayer[key].push(e);
    });

    // Pre-sort each group
    const sortedGroups: Record<string, MachineEvent[]> = {};
    for (const key in groupedByLayer) {
      sortedGroups[key] = groupedByLayer[key].sort(
        (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      );
    }

    // Pre-sort truck events
    const sortedTrucks = trucks.length > 0
      ? [...trucks].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
      : [];

    return { sortedGroupedEvents: sortedGroups, sortedTruckEvents: sortedTrucks };
  }, [events, truckEvents]);

  // Fast binary search to find event at a given time
  const findEventAtTime = useCallback((sortedEvents: MachineEvent[], time: number): MachineEvent | undefined => {
    let left = 0;
    let right = sortedEvents.length - 1;
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const start = new Date(sortedEvents[mid].startTime).getTime();
      const end = new Date(sortedEvents[mid].endTime).getTime();
      if (time >= start && time <= end) return sortedEvents[mid];
      if (time < start) right = mid - 1;
      else left = mid + 1;
    }
    return undefined;
  }, []);

  // Compute current events at a given time (uses pre-sorted data for speed)
  const getCurrentEvents = useCallback((time: number | null): {
    layers: Record<string, MachineEvent>;
    truck_event?: MachineEvent;
  } | null => {
    if (time === null) return null;

    const result: {
      layers: Record<string, MachineEvent>;
      truck_event?: MachineEvent;
    } = { layers: {} };

    // Find matching event in each layer
    for (const key in sortedGroupedEvents) {
      const match = findEventAtTime(sortedGroupedEvents[key], time);
      if (match) result.layers[key] = match;
    }

    // Find matching truck event
    if (sortedTruckEvents.length > 0) {
      const truckEvent = findEventAtTime(sortedTruckEvents, time);
      result.truck_event = truckEvent;
      if (truckEvent) {
        result.layers['truck'] = truckEvent;
      }
    }

    return result;
  }, [sortedGroupedEvents, sortedTruckEvents, findEventAtTime]);

  // Recompute currentEvents from the cursor store (machine-detail render
  // consumers). Skipped entirely when `trackCurrentEvents` is false (the `/map`
  // path has zero consumers, so this per-frame computation is pure waste there).
  // Update strategy mirrors the old effect exactly:
  // - "internal" (video playback, cursor movement): RAF-throttled fast path
  // - "external" (user selection): 500ms-debounced path
  // Driven by a store subscription (no per-tick re-render of the provider) plus a
  // recompute on data changes (the effect's `events`/`truckEvents`/`getCurrentEvents`
  // deps re-run it, matching the old effect's data-change behavior).
  useEffect(() => {
    if (!trackCurrentEvents) return;
    if (!events && !truckEvents) return;

    // Skip the state write when the resolved events are unchanged: during
    // same-event playback getCurrentEvents returns the SAME event refs, so this
    // bails the ~30x/sec no-op re-renders — currentEvents changes only when the
    // cursor crosses an event boundary.
    const applyCurrentEvents = () => {
      const next = getCurrentEvents(cursorStore.getSnapshot().selectedTime);
      setCurrentEvents(prev => (sameCurrentEvents(prev, next) ? prev : next));
    };

    const schedule = (origin: "internal" | "external" | "video") => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      if (origin === "internal") {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          applyCurrentEvents();
        });
      } else {
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          applyCurrentEvents();
        }, 500);
      }
    };

    const unsubscribe = cursorStore.subscribe(() => schedule(cursorStore.getSnapshot().lastOrigin));
    // Recompute once now — covers mount and (re)runs on data changes, exactly
    // like the old effect's `events`/`truckEvents` deps did.
    schedule(cursorStore.getSnapshot().lastOrigin);

    return () => {
      unsubscribe();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [trackCurrentEvents, events, truckEvents, getCurrentEvents, cursorStore]);

  // Memoize context value to prevent unnecessary re-renders of all consumers.
  // The cursor (`selectedTime`/`lastOrigin`) is deliberately NOT here — it lives
  // in `cursorStore`, so this value no longer changes on every playback tick.
  const contextValue = useMemo(() => ({
    setSelectedTime,
    currentEvents,
    selectedTrack,
    setSelectedTrack,
    isTrackLocked,
    setIsTrackLocked,
    isEditMode,
    setIsEditMode,
    selectedEventForEdit,
    setSelectedEventForEdit,
    createStartTime,
    setCreateStartTime,
    isVideoPlaying,
    setIsVideoPlaying,
    isVideoLinked,
    setIsVideoLinked,
    videoPlaybackRate,
    setVideoPlaybackRate,
    centerRequest,
    requestCenter,
    playRequest,
    requestPlay,
    pauseRequest,
    requestPause,
    eventOverlayRequestTime,
    requestEventOverlay,
  }), [
    setSelectedTime,
    currentEvents,
    selectedTrack,
    setSelectedTrack,
    isTrackLocked,
    setIsTrackLocked,
    isEditMode,
    setIsEditMode,
    selectedEventForEdit,
    setSelectedEventForEdit,
    createStartTime,
    setCreateStartTime,
    isVideoPlaying,
    setIsVideoPlaying,
    isVideoLinked,
    setIsVideoLinked,
    videoPlaybackRate,
    setVideoPlaybackRate,
    centerRequest,
    requestCenter,
    playRequest,
    requestPlay,
    pauseRequest,
    requestPause,
    eventOverlayRequestTime,
    requestEventOverlay,
  ]);

  return { value: contextValue, cursorStore };
};

/**
 * Standard provider — owns fresh timeline state and provides it to `children`.
 * Used by machine-detail and by the map's shell + inactive stack rows.
 */
export const TimelineProvider = ({
  children,
  events,
  truckEvents,
}: {
  children: ReactNode;
  events?: MachineEvent[];
  truckEvents?: MachineEvent[];
}) => {
  const { value, cursorStore } = useTimelineProviderValue(events, truckEvents);
  return (
    <CursorStoreContext.Provider value={cursorStore}>
      <TimelineContext.Provider value={value}>{children}</TimelineContext.Provider>
    </CursorStoreContext.Provider>
  );
};

export const useTimelineContext = (): TimelineContextType => {
  const context = useContext(TimelineContext);
  if (!context) {
    throw new Error("useTimelineContext must be used within a TimelineProvider");
  }
  return context;
};
